import { formatUIDate } from '@tiles-erp/shared';
import DownloadIcon from '@mui/icons-material/Download';
import { Alert, Button, FormControlLabel, Paper, Stack, Switch } from '@mui/material';
import type { ColDef } from 'ag-grid-community';
import { useMemo, useState } from 'react';
import { usePagination } from '@tiles-erp/hooks';
import type { SixOrbitStockExportRow } from '@tiles-erp/shared-types';
import { PageContainer } from '@tiles-erp/ui';
import { DataTable } from '../../components/DataTable';
import { SearchableSelect } from '../../components/SearchableSelect';
import { downloadFile } from '../../lib/download';
import { useSessionBranchId } from '../../lib/session-branch';
import { useCatalogOptions } from '../catalog/api';
import { useSixOrbitStockExport } from '../inventory/api';
import { useBranches } from '../products/branch-prices-api';
import { useProductSizes } from '../products/api';

/** Current ERP stock laid out exactly as the supplied SixOrbit stock workbook. */
export function SixOrbitStockExportPage(): JSX.Element {
  const pagination = usePagination({ initialPageSize: 100 });
  const branches = useBranches();
  const [branchId, setBranchId] = useSessionBranchId();
  const [brandId, setBrandId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [sizeMm, setSizeMm] = useState('');
  const [showZeroStock, setShowZeroStock] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const brands = useCatalogOptions('/brands');
  const categories = useCatalogOptions('/categories', brandId || undefined);
  const sizes = useProductSizes();
  const filters = {
    branchId: branchId || undefined,
    brandId: brandId || undefined,
    categoryId: categoryId || undefined,
    sizeMm: sizeMm || undefined,
    showZeroStock: showZeroStock ? 'true' : undefined,
  };
  const report = useSixOrbitStockExport(pagination.query, filters);

  const columns = useMemo<ColDef<SixOrbitStockExportRow>[]>(() => [
    { field: 'variationId', headerName: 'variation id', minWidth: 135 },
    { field: 'variationNo', headerName: 'variation no', minWidth: 135 },
    { field: 'variationName', headerName: 'Varation name', minWidth: 260 },
    { field: 'brand', headerName: 'Brand', minWidth: 170 },
    { field: 'size', headerName: 'Size', minWidth: 120 },
    { field: 'warehouse', headerName: 'warehouse', minWidth: 180 },
    { field: 'stock', headerName: 'stock', minWidth: 110, type: 'numericColumn' },
    { field: 'price', headerName: 'price', minWidth: 110, type: 'numericColumn', valueFormatter: (p) => Number(p.value ?? 0).toFixed(2) },
    { field: 'location', headerName: 'Location', minWidth: 180 },
    { field: 'defaultVendor', headerName: 'Defalut vendor', minWidth: 220 },
    { field: 'date', headerName: 'Date', minWidth: 130, valueFormatter: (p) => p.value ? formatUIDate(new Date(p.value as string)) : '' },
  ], []);

  const download = async (): Promise<void> => {
    setDownloading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries({ ...filters, search: pagination.query.search })) {
        if (value) params.set(key, value);
      }
      await downloadFile(`/stock/sixorbit-export.xlsx?${params.toString()}`, `sixorbit-stock-${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The SixOrbit stock workbook could not be downloaded.');
    } finally {
      setDownloading(false);
    }
  };

  return <PageContainer
    title="SixOrbit stock export"
    subtitle="Current godown stock in the supplied SixOrbit Excel layout. Stock and price are exported per piece."
    actions={<Button variant="contained" startIcon={<DownloadIcon />} disabled={downloading || !report.data?.meta.totalItems}
      onClick={() => void download()}>{downloading ? 'Preparing…' : 'Download Excel'}</Button>}
  >
    <Stack spacing={1.5}>
      {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
      {report.error && <Alert severity="error">{report.error.message}</Alert>}
      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
          <SearchableSelect label="Branch" value={branchId} allLabel="All accessible branches"
            options={(branches.data ?? []).map((branch) => ({ value: branch.id, label: branch.name }))}
            onChange={(value) => { setBranchId(value); pagination.setPage(1); }} width={{ xs: '100%', sm: 220 }} />
          <SearchableSelect label="Brand" value={brandId} allLabel="All brands"
            options={(brands.data ?? []).map((brand) => ({ value: brand.id, label: brand.name }))}
            onChange={(value) => { setBrandId(value); setCategoryId(''); pagination.setPage(1); }} width={{ xs: '100%', sm: 210 }} />
          <SearchableSelect label="Category" value={categoryId} allLabel="All categories"
            options={(categories.data ?? []).map((category) => ({ value: category.id, label: category.name }))}
            onChange={(value) => { setCategoryId(value); pagination.setPage(1); }} width={{ xs: '100%', sm: 230 }} />
          <SearchableSelect label="Size" value={sizeMm} allLabel="All sizes"
            options={(sizes.data ?? []).map((size) => ({ value: size, label: size }))}
            onChange={(value) => { setSizeMm(value); pagination.setPage(1); }} width={{ xs: '100%', sm: 170 }} />
          <FormControlLabel
            control={<Switch checked={showZeroStock} onChange={(event) => {
              setShowZeroStock(event.target.checked);
              pagination.setPage(1);
            }} />}
            label="Show zero stock items"
          />
        </Stack>
      </Paper>
      <DataTable rows={report.data?.items ?? []} columns={columns} meta={report.data?.meta}
        pagination={pagination} loading={report.isLoading || report.isFetching}
        searchPlaceholder="Search variation no, product or SKU…" exportable={false} height={650} />
    </Stack>
  </PageContainer>;
}
